import {
  hotelFilterableOffersOf,
  matchesHotelResultsFilters,
  sortHotelOffers,
  type HotelFilterableOffer,
  type HotelOfferView,
  type HotelResultsFilterValue,
  type HotelResultsFiltersCopy,
  type HotelResultsOrder,
} from '@voll-intelligence/intelligence-ui-kit';
import type { useGenUiI18n } from '../../../shared/ui/i18n.js';
import {
  hotelDistanceKm,
  type ChatUiHotelOffer,
  type HotelPlannerPlace,
} from '@voll-intelligence/genui-contract';

/** Os fatos de corte e de ordem das linhas (`hotelFilterableOffersOf`, no kit),
 *  medidos com a régua de distância do wire. */
export function filterableHotelOffers(
  offers: readonly HotelOfferView[],
  sourceOffers: readonly ChatUiHotelOffer[],
  nights: number | undefined,
  place: HotelPlannerPlace | undefined,
  currency: string,
): HotelFilterableOffer[] {
  return hotelFilterableOffersOf(offers, sourceOffers, {
    nights,
    currency,
    distanceOf: (offer) => hotelDistanceKm(offer, place),
  });
}

/**
 * As linhas que a TELA mostra: `views` depois do corte de CLIENTE e da ordem —
 * o MESMO predicado (`matchesHotelResultsFilters`) e a MESMA ordenação
 * (`sortHotelOffers`) que a lista do ui-kit aplica sobre o que carregou.
 *
 * Existe para o relato do painel ao agente (#2857, dev 19/09): ele contava
 * `views` — a lista da PÁGINA, onde o corte de servidor "manda a mais" e ainda
 * nem chegou quando o viajante fecha o painel logo depois de filtrar — e dizia
 * "6 visíveis" para um painel que mostrava "1 de 8"; o agente respondia
 * "sobraram 6". O que o agente lê tem de ser o que o viajante vê.
 */
export function shownHotelViews(
  views: readonly HotelOfferView[],
  sourceOffers: readonly ChatUiHotelOffer[],
  filters: HotelResultsFilterValue,
  order: HotelResultsOrder,
  context: {
    nights: number | undefined;
    place: HotelPlannerPlace | undefined;
    currency: string;
  },
): HotelOfferView[] {
  const facts = filterableHotelOffers(
    views,
    sourceOffers,
    context.nights,
    context.place,
    context.currency,
  );
  return sortHotelOffers(
    facts.filter((offer) => matchesHotelResultsFilters(offer, filters)),
    order,
  ).map(({ offer }) => offer);
}

export function hotelResultsFiltersCopy(
  t: ReturnType<typeof useGenUiI18n>['t'],
  locale: string,
): HotelResultsFiltersCopy {
  return {
    filtersLabel: t('hotel.results.filters'),
    collapseFiltersLabel: t('hotel.results.collapseFilters'),
    closeFiltersLabel: t('hotel.results.closeFilters'),
    applyFiltersLabel: t('hotel.results.applyFilters'),
    resetFiltersLabel: t('hotel.results.resetFilters'),
    policyLabel: t('hotel.results.policy'),
    withinPolicyLabel: t('hotel.results.withinPolicy'),
    rateLabel: t('hotel.results.rate'),
    breakfastLabel: t('hotel.results.breakfast'),
    refundableLabel: t('hotel.results.refundable'),
    agreedRateLabel: t('hotel.results.agreedRate'),
    nightlyPriceLabel: t('hotel.results.nightlyPrice'),
    minimumPriceLabel: t('hotel.results.minimumPrice'),
    maximumPriceLabel: t('hotel.results.maximumPrice'),
    ratingLabel: t('hotel.results.rating'),
    distanceLabel: t('hotel.results.distance'),
    amenitiesLabel: t('hotel.results.amenities'),
    commandPlaceholder: t('hotel.results.askPlaceholder'),
    sendLabel: t('hotel.results.send'),
    distanceUpToLabel: (distance) => t('hotel.results.distanceUpTo', { distance }),
    ratingThresholdLabel: (rating) =>
      `${rating.toLocaleString(locale, { minimumFractionDigits: 1 })}+`,
    // Só números formatados no locale — sem chave de i18n, como o "+" acima.
    ratingBandLabel: (from, upTo) => {
      const format = (value: number) =>
        value.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      return `${format(from)} – ${format(upTo)}`;
    },
  };
}
